import { useLayoutEffect, useState } from 'react';

/**
 * useHeroBarHeight
 * 
 * Dynamically measures the height of the hero bar element.
 * Assumes the hero bar is the element immediately before <nav> in the DOM.
 * 
 * Uses ResizeObserver for efficient size change detection,
 * requestAnimationFrame for layout safety, and proper cleanup to prevent
 * memory leaks and stale callbacks.
 * 
 * Performance optimizations:
 * - useLayoutEffect ensures synchronous measurement before browser paint
 * - RAF batching prevents multiple queued updates
 * - Equality check prevents redundant re-renders
 * - Proper cleanup of observers and pending animation frames
 * 
 * @returns {number} The current hero bar height in pixels (0 if not found)
 */
export const useHeroBarHeight = (): number => {
  const [heroBarHeight, setHeroBarHeight] = useState(0);

  useLayoutEffect(() => {
    // SSR safety guard
    if (typeof window === 'undefined') return;

    const nav = document.querySelector('nav');
    const heroBar = nav?.previousElementSibling as HTMLElement | null;

    // Early return if elements not found
    if (!nav || !heroBar) return;

    let frameId: number;

    const updateHeight = () => {
      const hasContent = heroBar.children.length > 0 || heroBar.textContent?.trim();
      const newHeight = hasContent ? heroBar.scrollHeight || 0 : 0;

      // Prevent redundant re-renders by only updating if height changed
      setHeroBarHeight(prev => (prev === newHeight ? prev : newHeight));
    };

    const observer = new ResizeObserver(() => {
      // Cancel any pending frame to batch updates efficiently
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updateHeight);
    });

    observer.observe(heroBar);
    updateHeight(); // Initial measurement

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameId); // Cleanup pending animation frames
    };
  }, []);

  return heroBarHeight;
};
